import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { apiCore } from "../api/apiCore";
import * as url from "../api/urls";
import { useSocket } from "./SocketContext";
import { playNotification, resolveLastMessageTime, vibrate } from "../utils";
import { IContact } from "../types/contacts";
import { getContactList } from "../api";
import { useAuth } from "../context/AuthContext";

export interface ContactsContextType {
  contacts: IContact[];
  contactsMap: Map<number, IContact>;
  contactsByPhoneMap: Map<string, IContact>;
  loading: boolean;
  error: string | null;

  incrementUnread: (idChatSession: number, amount?: number) => void;
  getContacts: () => Promise<IContact[]>;
  addContact: (data: IContact) => Promise<IContact | null>;
  updateContact: (data: IContact) => Promise<IContact | null>;
  deleteContact: (id: number) => Promise<void>;
  contactsImage: (data: any) => Promise<IContact | null>;
  contactsVisit: (data: any) => Promise<IContact | null>;
  contactsMarked: (data: any) => Promise<IContact | null>;
  contactsService: (data: any) => Promise<IContact | null>;
}

// -------------------------------------
// CONTEXT
// -------------------------------------
export const ContactsContext = createContext<ContactsContextType | null>(null);

export const ContactsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pendingContactsRef = useRef<IContact[]>([]);

  // ---------------------------------------------
  // Mata lista de contato ao LOGOFF
  // ---------------------------------------------
  useEffect(() => {
    if (!user) {
      setContacts([]);
      setLoading(false);
    }
  }, [user]);

  // ---------------------------------------------
  // CARREGA LISTA INICIAL
  // ---------------------------------------------
  useEffect(() => {
    if (user?.idEnterprise) {
      getContacts();
    }
  }, [user?.idEnterprise]);


  //Por Celular
  const contactsByPhoneMap = useMemo(() => {
    const map = new Map<string, IContact>();
    contacts.forEach(c => {
      map.set(c.phoneNumber, c);
    });

    return map;
  }, [contacts]);

  //Por ID
  const contactsMap = useMemo(() => {
    const map = new Map<number, IContact>();
    contacts.forEach(c => {
      map.set(c.id, c);
    });

    return map;
  }, [contacts]);

  // ---------------------------------------------
  // GET CONTACTS (API)
  // ---------------------------------------------
  const getContacts = useCallback(
    async (): Promise<IContact[]> => {
      if (!user?.idEnterprise) {
        // console.warn("Usuário ou idEnterprise não disponível", user);
        return [];
      }

      try {
        setLoading(true);
        console.log('[context] getContacts');

        const response = await getContactList(user.idEnterprise);

        setContacts(prev => {

          const mapPrev = new Map(prev.map(c => [c.id, c]));

          let merged = response.map(newItem => {
            const oldItem = mapPrev.get(newItem.id);

            // 🔥 mantém referência se não mudou
            if (oldItem && JSON.stringify(oldItem) === JSON.stringify(newItem)) {
              return oldItem;
            }

            return newItem;
          });

          // 🔥 continua tratando buffer do SignalR
          if (pendingContactsRef.current.length > 0) {

            console.log('[SignalR] merging buffered contacts...');

            pendingContactsRef.current.forEach(buffered => {

              const index = merged.findIndex(c => c.id === buffered.id);

              if (index === -1) {
                merged.unshift(buffered);
              } else {
                merged[index] = {
                  ...merged[index],
                  ...buffered
                };
              }

            });

            pendingContactsRef.current = [];
          }

          return merged;
        });

        // setContacts(prev => {

        //   let merged = [...response];

        //   if (pendingContactsRef.current.length > 0) {

        //     console.log('[SignalR] merging buffered contacts...');

        //     pendingContactsRef.current.forEach(buffered => {

        //       const index = merged.findIndex(c => c.id === buffered.id);

        //       if (index === -1) {
        //         merged.unshift(buffered);
        //       } else {
        //         merged[index] = {
        //           ...merged[index],
        //           ...buffered
        //         };
        //       }

        //     });

        //     pendingContactsRef.current = [];
        //   }

        //   return merged;
        // });

        return response;
      } catch (e) {
        console.error(e);
        setError("Erro ao carregar contatos");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [user] // 👈 ESSENCIAL
  );

  // ---------------------------------------------
  // CRUD
  // ---------------------------------------------
  const addContact = useCallback(
    async (data: IContact): Promise<IContact | null> => {
      const created = await apiCore<IContact>(url.GET_CONTACTS, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (created.status === 200) {
        setContacts((prev) => [...prev, created.data]);
        return created.data; // ✅ retorna IContact
      }

      return null;
    },
    []
  );

  const updateContact = useCallback(async (data: IContact) => {
    const updated = await apiCore<IContact>(url.GET_CONTACTS, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (updated.status === 200) {
      setContacts((prev) => prev.map((c) => (c.id === data.id ? updated.data : c)));
      return updated.data; // ✅ retorna IContact
    }

    return null;

  }, []);

  const deleteContact = useCallback(async (id: number) => {
    await apiCore(`${url.GET_CONTACTS}/${id}`, { method: "DELETE" });
    setContacts((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const contactsImage = useCallback(
    async (data: any): Promise<IContact | null> => {
      console.log('contactsImage', data);

      const updated = await apiCore<IContact>(`${url.GET_CONTACTS}/ContactsImage`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (updated.status === 200) {
        setContacts((prev) => prev.map((c) => (c.id === data.id ? updated.data : c)));
        return updated.data; // ✅ retorna IContact
      }

      return null;
    },
    []
  );

  const contactsVisit = useCallback(
    async (data: any): Promise<IContact | null> => {
      console.log('contactsImage', data);

      const updated = await apiCore<IContact>(`${url.GET_CONTACTS}/ContactsVisit`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (updated.status === 200) {
        setContacts((prev) => prev.map((c) => (c.id === data.id ? updated.data : c)));
        return updated.data; // ✅ retorna IContact
      }

      return null;
    },
    []
  );

  const contactsMarked = useCallback(
    async (data: any): Promise<IContact | null> => {
      // console.log('contactsMarked', data);

      const updated = await apiCore<IContact>(`${url.GET_CONTACTS}/contactsMarked`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (updated.status === 200) {
        setContacts((prev) => prev.map((c) => (c.id === data.id ? updated.data : c)));
        return updated.data; // ✅ retorna IContact
      }

      return null;
    },
    []
  );

  const contactsService = useCallback(
    async (data: any): Promise<IContact | null> => {
      console.log('contactsService', data);

      const updated = await apiCore<IContact>(`${url.GET_CONTACTS}/contactsService`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (updated.status === 200) {
        setContacts((prev) => prev.map((c) => (c.id === data.id ? updated.data : c)));
        return updated.data; // ✅ retorna IContact
      }

      return null;
    },
    []
  );

  const incrementUnread = useCallback((idContact: number) => {


    setContacts(prev => {

      const index = prev.findIndex(c => c.id === idContact);
      if (index === -1) return prev;

      const existing = prev[index];

      const updated: IContact = {
        ...existing,
        unreadCount: (existing.unreadCount ?? 0) + 1
      };

      const without = prev.filter((_, i) => i !== index);

      return [updated, ...without];
    });

  }, []);


  // ---------------------------------------------
  // SIGNALR 🔥
  // ---------------------------------------------
  const { connection, subscribeEvent } = useSocket();


  // Função genérica para adicionar ou atualizar contato
  const upsertContactSignalR = (isNotify: boolean, incoming: IContact) => {


    setContacts(prev => {

      const index = prev.findIndex(c => c.id === incoming.id);

      // 🟢 NOVO
      if (index === -1) {

        const updated: IContact = {
          ...incoming,
          unreadCount: 1,
          lastMessageTime:
            incoming.lastMessageTime ??
            new Date().toISOString(), // 👈 fallback
        };

        return [updated, ...prev];
      }

      // 🟡 UPDATE
      const existing = prev[index];

      const updated: IContact = {
        ...existing,
        ...incoming,
        unreadCount: existing.unreadCount ?? 0,
        lastMessageTime: resolveLastMessageTime(
          existing.lastMessageTime,
          incoming.lastMessageTime
        ),
      };

      const without = prev.filter((_, i) => i !== index);

      if (isNotify) {
        playNotification();
      }

      // 🔥 SOBE SEM PERDER ORDEM
      return [updated, ...without];
    });
  };

  useEffect(() => {
    if (!connection || connection.state !== "Connected" || !user) {
      console.log("[SignalR] aguardando conexão válida para subscribe...");
      return;
    }

    // console.log('🟢 Subscribing SignalR events Contact');

    const unsubNew = subscribeEvent<{ contact: IContact }>("MessageNewContact", (data) => {
      // console.log("[ContactsContext] MessageNewContact", data.contact);
      upsertContactSignalR(true, data.contact)
    }
    );

    const unsubUpdate = subscribeEvent<{ contact: IContact }>("MessageUpdateContact", (data) => {
      // console.log("[ContactsContext] MessageUpdateContact", data.contact);
      upsertContactSignalR(false, data.contact)
    }
    );

    return () => {
      // console.log("[SignalR] Unsubscribing contacts...");
      unsubNew();   // cleanup seguro
      unsubUpdate();
    };
  }, [user?.id, connection?.state,  // 👈 ESSENCIAL  , connection, user
    subscribeEvent]);

  // ---------------------------------------------
  // MEMO
  // ---------------------------------------------
  const value = useMemo(
    () => ({
      contacts,
      loading,
      error,
      contactsMap,
      contactsByPhoneMap,

      incrementUnread,
      getContacts,
      addContact,
      updateContact,
      deleteContact,
      contactsImage,
      contactsVisit,
      contactsMarked,
      contactsService,
    }),
    [contacts, loading, error, contactsMap, contactsByPhoneMap,
      incrementUnread, getContacts, addContact, updateContact, deleteContact, contactsImage, contactsVisit, contactsMarked, contactsService]
  );

  return <ContactsContext.Provider value={value}>{children}</ContactsContext.Provider>;
};

// HOOK
export const useContacts = () => {
  const ctx = useContext(ContactsContext);
  if (!ctx) throw new Error("useContacts must be used within ContactsProvider");
  return ctx;
};
